import React from "react";
// 1. Import the new modular week file
import { week_2026_01_05 } from "./week_2026_01_05";

// 2. Import UI components needed for the historical data (Modals, etc.)
import { Section, Code, Button, Badge, Disclosure } from "../components/UI";

// --- HISTORICAL DATA (Dec 2025 - Nov 2025) ---

const week_2025_12_15 = {
  id: "WK-DEC15",
  date: "Dec 15, 2025",
  title: "Forrester Prep & SDR Tooling",
  attendees: "Marlee, Benton",
  summary:
    "Preparation for the Forrester analyst call, plus operationalizing the SDR tools (specifically NVIDIA bio scraping).",
  tags: ["Strategy", "Forrester", "SDR Tools"],
  deliverables: [
    {
      id: "d1",
      text: "Create 1-page brief for Forrester (Summary + Screenshots)",
      owner: "Ben",
      links: [
        {
          label: "View Brief",
          url: "https://forrestercallprep.fairwaydigitalmedia.com/",
        },
      ],
    },
    {
      id: "d2",
      text: "Submit final Forrester questions via Slack",
      owner: "Ben",
      defaultChecked: true,
    },
    {
      id: "d2_b",
      text: "Operationalize SDR Tools (Scrape NVIDIA CTO Bios)",
      owner: "Ben",
      links: [
        {
          label: "View Engagement Strategy",
          actionUrl:
            "https://coursera.fairwaydigitalmedia.com/#/tools/social-comments-playbook",
          actionLabel: "View Live NVIDIA Playbook",
          modalContent: (
            <>
              <div style={{ marginBottom: "24px" }}>
                <h1
                  className="text-heading-lg"
                  style={{ marginBottom: "4px" }}
                >
                  Persona Engagement Strategy: NVIDIA
                </h1>
                <p className="text-body" style={{ color: "var(--ink-soft)" }}>
                  We mapped the live social graph from NVIDIA's CTO (Michael
                  Kagan) to key L&D and Engineering leaders to create a
                  high-intent outreach list.
                </p>
              </div>

              <Section index="1" label="The Trigger Event">
                <p style={{ marginBottom: "8px" }}>
                  <strong>Pat Grady (Sequoia) Post:</strong> Interview with
                  NVIDIA CTO Michael Kagan discussing "Inference vs. Training"
                  compute loads.
                </p>
                <ul style={{ paddingLeft: "20px", margin: 0 }}>
                  <li style={{ marginBottom: "4px" }}>
                    <strong>Signal:</strong> High engagement from engineering
                    leaders worried about skill gaps in "inference"
                    infrastructure.
                  </li>
                  <li>
                    <strong>Opportunity:</strong> Position Coursera's new
                    GenAI/CUDA tracks as the solution.
                  </li>
                </ul>
              </Section>

              <Section index="2" label="The Workflow">
                <p>
                  We used the "Social Like Miner" to extract 90+ relevant leads
                  who engaged with this specific post.
                </p>
                <div
                  style={{
                    marginTop: "12px",
                    padding: "12px",
                    background: "#F2F9E5",
                    borderRadius: "8px",
                    border: "1px solid #76B900",
                  }}
                >
                  <strong style={{ color: "#385900" }}>
                    Target Roles Identified:
                  </strong>
                  <ul style={{ margin: "8px 0 0 20px", color: "#385900" }}>
                    <li>Cindy H. Hoffmann (L&D @ Nvidia)</li>
                    <li>Karthick Iyer (VP Engineering @ Nvidia)</li>
                    <li>Deepu Talla (VP GM @ Nvidia)</li>
                  </ul>
                </div>
              </Section>

              <div style={{ marginTop: "24px", display: "flex", gap: "12px" }}>
                <Button
                  variant="primary"
                  onClick={() =>
                    window.open(
                      "https://coursera.fairwaydigitalmedia.com/#/tools/social-comments-playbook",
                      "_blank"
                    )
                  }
                >
                  View Interactive Playbook ↗
                </Button>
                <Button
                  variant="secondary"
                  onClick={() =>
                    window.open(
                      "https://coursera.fairwaydigitalmedia.com/#/tools/social-comments",
                      "_blank"
                    )
                  }
                >
                  Launch Social Tool ↗
                </Button>
              </div>
            </>
          ),
        },
      ],
    },
    {
      id: "d3",
      text: "Compile ABM EOY Summary Report (Marketing Ops/C4B Slide)",
      owner: "Marlee/Susie",
    },
    {
      id: "d4",
      text: "Research Best-in-Class ABM vs Marketing Ops structures",
      owner: "Ben",
    },
  ],
};

const week_2025_12_01 = {
  id: "WK-DEC01",
  date: "Dec 01, 2025",
  title: "SDR/AE Pilot & Custom Questions",
  attendees: "Marlee, Benton",
  summary:
    "Defining the pilot group (1 SDR, 1 AE), finalizing custom questions, and outlining the testing approach.",
  tags: ["Enablement", "Pilot", "Testing"],
  deliverables: [
    {
      id: "d5",
      text: "Create brief summary of approach for tests (Due Dec 1-5)",
      owner: "Ben",
      defaultChecked: true,
    },
    {
      id: "d5_b",
      text: "Confirm Pilot Participants (Samnit/Todd + Managers)",
      owner: "Marlee",
    },
    {
      id: "d6",
      text: "Finalize Custom Questions (Title/Article)",
      owner: "Ben + Team",
      defaultChecked: true,
      links: [
        {
          label: "View APAC KPIs",
          url: "https://globalc4bcampaigns.fairwaydigitalmedia.com/?tab=kpis",
        },
        {
          label: "View Questions",
          actionUrl:
            "https://networkinginfonet.com/Coursera/get-the-2025-global-skills-apac/1C029FA5-4210-4BCD-A58B-82B24982226A",
          actionLabel: "View Landing Page",
          modalContent: (
            <>
              <div style={{ marginBottom: "24px" }}>
                <p className="text-body" style={{ color: "var(--ink-soft)" }}>
                  Approved qualifying questions for the APAC campaign variants.
                </p>
              </div>

              <div style={{ display: "grid", gap: "24px" }}>
                <Section index="1" label="Variant 1: Strategic Priority">
                  <p
                    className="text-body"
                    style={{ fontWeight: 600, marginBottom: "12px" }}
                  >
                    "When considering an investment in skills training, what is
                    most important to your organization?"
                  </p>
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    <li style={{ marginBottom: "4px" }}>
                      Business growth potential
                    </li>
                    <li style={{ marginBottom: "4px" }}>
                      Ability to innovate quickly
                    </li>
                    <li style={{ marginBottom: "4px" }}>Cost efficiency</li>
                    <li style={{ marginBottom: "4px" }}>
                      Market competitiveness
                    </li>
                    <li style={{ marginBottom: "4px" }}>
                      Compliance requirements
                    </li>
                    <li
                      style={{
                        color: "var(--ink-lighter)",
                        fontStyle: "italic",
                      }}
                    >
                      No budget (Disqualifier)
                    </li>
                  </ul>
                </Section>

                <Section index="2" label="Variant 2: Evaluation Criteria">
                  <p
                    className="text-body"
                    style={{ fontWeight: 600, marginBottom: "12px" }}
                  >
                    "What factor is most important when evaluating/purchasing
                    skills training for your organization?"
                  </p>
                  <ul style={{ paddingLeft: "20px", margin: 0 }}>
                    <li style={{ marginBottom: "4px" }}>Cost per learner</li>
                    <li style={{ marginBottom: "4px" }}>Projected ROI</li>
                    <li style={{ marginBottom: "4px" }}>Alignment with KPIs</li>
                    <li style={{ marginBottom: "4px" }}>Compliance needs</li>
                    <li style={{ marginBottom: "4px" }}>
                      Training scalability
                    </li>
                    <li
                      style={{
                        color: "var(--ink-lighter)",
                        fontStyle: "italic",
                      }}
                    >
                      No budget (Disqualifier)
                    </li>
                  </ul>
                </Section>
              </div>
            </>
          ),
        },
      ],
    },
    {
      id: "d7",
      text: "Sync with Seleste & Ceyda regarding Enablement",
      owner: "Marlee",
    },
  ],
};

const week_2025_11_24 = {
  id: "WK-NOV24",
  date: "Nov 24, 2025",
  title: "Content Syndication Phase II + Sales Enablement",
  attendees: "Marlee, Benton",
  summary:
    "Mapping out Phase II NAMER syndication, reviewing ABM COE videos, and planning AI tool testing.",
  tags: ["Syndication", "Highspot", "Video"],
  deliverables: [
    {
      id: "d8_a",
      text: "Launch 2026 ABM Planning Suite (Tooling)",
      owner: "Marlee + Ben",
      defaultChecked: true,
      links: [
        {
          label: "View Planning Suite",
          url: "https://abmannualplanningtool.fairwaydigitalmedia.com/",
        },
        {
          label: "Read Strategy Brief",
          actionUrl: "https://abmannualplanningtool.fairwaydigitalmedia.com/",
          actionLabel: "Launch Planner",
          modalContent: (
            <>
              <div style={{ marginBottom: "24px" }}>
                <h1
                  className="text-heading-lg"
                  style={{ marginBottom: "4px" }}
                >
                  Coursera 2026 ABM Strategic Planning Suite
                </h1>
                <p className="text-body" style={{ color: "var(--ink-soft)" }}>
                  To support our collaborative planning for 2026, we have
                  developed a custom, interactive ABM Planning Suite. This moves
                  beyond static spreadsheets to facilitate real-time scenario
                  planning.
                </p>
              </div>

              <Section index="1" label="Program Mix Scenario Planner">
                <p style={{ marginBottom: "8px" }}>
                  <strong>Goal:</strong> Move from "How much are we spending?"
                  to "What outcomes are we buying?"
                </p>
                <p>
                  We can dynamically adjust the weight of investment across
                  Brand, Demand Creation, Sales Enablement, and Market
                  Intelligence to instantly forecast expected outputs
                  (Impressions, Leads, Contacts).
                </p>
              </Section>

              <Section index="2" label="Global Content Syndication Dashboard">
                <p style={{ marginBottom: "8px" }}>
                  <strong>Goal:</strong> Ensure regional balance between volume
                  and quality targets.
                </p>
                <p>
                  Visualizes regional guarantees (NAMER, India, SEA)
                  side-by-side to ensure full visibility into volume
                  distribution, allowing us to balance or shift targets flexibly
                  based on regional priorities.
                </p>
              </Section>

              <Section index="3" label="Strategic Account Mapping">
                <p style={{ marginBottom: "8px" }}>
                  <strong>Goal:</strong> Operationalize strategies for
                  high-value accounts (e.g. Vanguard, Nvidia).
                </p>
                <p>
                  Includes Account Plans and an interactive Buying Committee
                  Visualizer to map stakeholders (CDAO, CHRO) and tag them by
                  sentiment.
                </p>
              </Section>

              <div
                style={{
                  background: "#F2F9E5",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "1px solid #76B900",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    color: "#385900",
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  Strategic Value for 2026
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#385900",
                    lineHeight: "1.5",
                  }}
                >
                  "This tool allows Coursera and Fairway Consulting to
                  collaborate together and tweak numbers live—validating our
                  assumptions and finalizing allocations in real-time."
                </p>
              </div>
            </>
          ),
        },
      ],
    },
    {
      id: "d8_b",
      text: "Finalize Phase II NAMER Content Syndication Plan",
      owner: "Marlee + Ben",
      defaultChecked: false,
    },
    {
      id: "d8",
      text: "Post ABM COE Videos + Resources to Highspot",
      owner: "Marlee",
    },
    {
      id: "d9",
      text: "Develop plan for testing AI tools (Social Comments Tool)",
      owner: "Ben + Marlee",
      defaultChecked: true,
      links: [
        {
          label: "View Tool Suite",
          actionUrl:
            "https://coursera.fairwaydigitalmedia.com/#/tools/social-comments",
          actionLabel: "Open Dashboard",
          modalContent: (
            <>
              <div style={{ marginBottom: "24px" }}>
                <h1
                  className="text-heading-lg"
                  style={{ marginBottom: "4px" }}
                >
                  Social Selling & AI Tooling Plan
                </h1>
                <p className="text-body" style={{ color: "var(--ink-soft)" }}>
                  We have developed a suite of tools to help SDRs identify
                  high-intent social signals and map them to specific buyer
                  personas.
                </p>
              </div>

              <Section index="1" label="Signal Capture: The 'Like' Miner">
                <p style={{ marginBottom: "12px" }}>
                  Identify high-value problem-aware posts and filter the
                  reaction list to find leads in seconds.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    marginBottom: "8px",
                  }}
                >
                  <Button
                    variant="secondary"
                    onClick={() =>
                      window.open(
                        "https://coursera.fairwaydigitalmedia.com/#/tools/social-comments",
                        "_blank"
                      )
                    }
                  >
                    Open Social Tool ↗
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      window.open(
                        "https://coursera.fairwaydigitalmedia.com/#/tools/social-comments-playbook",
                        "_blank"
                      )
                    }
                  >
                    View Playbook ↗
                  </Button>
                </div>
              </Section>

              <Section index="2" label="Persona Engagement Guides">
                <p style={{ marginBottom: "12px" }}>
                  Interactive carousels designed to help SDRs map value props to
                  specific buyer personas.
                </p>
                <div style={{ display: "grid", gap: "12px" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#F9FAFB",
                      padding: "12px",
                      borderRadius: "8px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      Financial Services Persona
                    </span>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        window.open(
                          "https://coursera.fairwaydigitalmedia.com/#/tools/fs-persona-carousel",
                          "_blank"
                        )
                      }
                    >
                      Launch Carousel ↗
                    </Button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background: "#F9FAFB",
                      padding: "12px",
                      borderRadius: "8px",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      Tech / Software Persona
                    </span>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        window.open(
                          "https://coursera.fairwaydigitalmedia.com/#/tools/tech-persona-carousel",
                          "_blank"
                        )
                      }
                    >
                      Launch Carousel ↗
                    </Button>
                  </div>
                </div>
              </Section>
            </>
          ),
        },
      ],
    },
    {
      id: "d10",
      text: "Recruit 1-2 SDRs and AEs for pilot group",
      owner: "Marlee",
    },
  ],
};

const week_2025_11_17 = {
  id: "WK-NOV17",
  date: "Nov 17, 2025",
  title: "Gatekeeper Security & Content Translation",
  attendees: "Marlee, Benton",
  summary:
    "Implemented Coursera-only gate access, confirmed content translation impacts on landing pages, and delivered final video assets to Marlee.",
  tags: ["Localization", "Access", "Assets"],
  deliverables: [
    {
      id: "d11_b",
      text: "Confirm translation impact on landing pages",
      owner: "Marlee",
    },
    {
      id: "d11",
      text: "Send video files to Marlee",
      owner: "Ben",
      defaultChecked: true,
    },
    {
      id: "d12",
      text: "Finalize Coursera-only access & privacy checks",
      owner: "Ben",
      links: [
        {
          label: "Read Implementation Details",
          actionUrl: "https://coursera.fairwaydigitalmedia.com/",
          actionLabel: "Visit Home Page",
          modalContent: (
            <>
              <div style={{ marginBottom: "24px" }}>
                <h1
                  className="text-heading-lg"
                  style={{ marginBottom: "4px" }}
                >
                  Security & Access Controls — Implementation Summary
                </h1>
                <p className="text-body" style={{ color: "var(--ink-soft)" }}>
                  We implemented sign-in, permissions, link protection, and
                  session continuity to keep sensitive microsite content secure
                  while keeping common resources easy to reach.
                </p>
              </div>

              <Section index="1" label="Domain-Level Access (Gatekeeper)">
                <p style={{ marginBottom: "8px" }}>
                  We verify the user’s email domain before any restricted
                  content loads.
                </p>
                <ul style={{ paddingLeft: "20px", margin: 0 }}>
                  <li style={{ marginBottom: "8px" }}>
                    <strong>VIP/Staff by default:</strong> Logins from{" "}
                    <Code>@coursera.org</Code> or{" "}
                    <Code>@fairwaydigitalmedia.com</Code> are automatically
                    granted VIP/Staff access.
                  </li>
                  <li>
                    <strong>Partner allowlist:</strong> Specific external
                    addresses (agencies/contractors) can be added on request.
                  </li>
                </ul>
              </Section>

              <Section index="2" label="Tool-Specific Visibility">
                <p style={{ marginBottom: "8px" }}>
                  Navigation and pages respect privacy per tool.
                </p>
                <ul style={{ paddingLeft: "20px", margin: 0 }}>
                  <li style={{ marginBottom: "8px" }}>
                    <strong>Public items:</strong> Standard resources remain
                    open.
                  </li>
                  <li style={{ marginBottom: "8px" }}>
                    <strong>Restricted items:</strong> Sensitive assets are
                    flagged <Code>restricted: true</Code> and hidden from
                    guests.
                  </li>
                  <li>
                    <strong>Smart menus:</strong> The ABM Tools menu
                    automatically hides restricted links for guest users.
                  </li>
                </ul>
              </Section>

              <Section index="3" label="Direct-Link Protection">
                <p>
                  <strong>No “backdoor” access:</strong> If someone opens a
                  restricted URL without logging in, the app intercepts,
                  explains the issue, and safely redirects them to the dashboard
                  to sign in.
                </p>
              </Section>

              <Section index="4" label="Persistent Sessions & Status Bar">
                <p style={{ marginBottom: "8px" }}>
                  <strong>Fewer re-logins:</strong> Browser storage remembers
                  validated sessions.
                </p>
                <p>
                  <strong>Clear status indicator:</strong> A masthead shows
                  access level at a glance:
                  <span
                    style={{
                      display: "inline-flex",
                      gap: "8px",
                      marginLeft: "12px",
                      verticalAlign: "middle",
                    }}
                  >
                    <Badge variant="warn">🔒 Staff Login</Badge>
                    <span
                      style={{
                        fontSize: "12px",
                        color: "var(--ink-soft)",
                      }}
                    >
                      vs.
                    </span>
                    <Badge variant="success">✅ Access Granted</Badge>
                  </span>
                </p>
              </Section>

              <Disclosure title="Why this matters">
                “These updates protect sensitive partner content while keeping
                everyday resources simple to access—so your team can share
                confidently with fewer access issues.”
              </Disclosure>
            </>
          ),
        },
      ],
    },
  ],
};

const week_2025_11_14 = {
  id: "WK-NOV14",
  date: "Nov 14, 2025",
  title: "ABM COE Resources & 2026 Planning",
  attendees: "Marlee, Benton",
  summary:
    "First pass of ABM COE resources, incorporating feedback into 2026 planning, and APAC syndication readiness.",
  tags: ["Planning", "APAC", "COE"],
  deliverables: [
    {
      id: "d13",
      text: "Draft Agenda/Questions for Dec Pilot Group",
      owner: "Ben + Marlee",
    },
    {
      id: "d13_b",
      text: "Review & Finalize ABM COE Resources",
      owner: "Marlee + Ben",
    },
    {
      id: "d13_c",
      text: "Incorporate ABM Feedback into 2026 Planning",
      owner: "Ben + Marlee",
    },
    {
      id: "d14",
      text: "Send Job Titles to Shatakshi",
      owner: "Marlee",
      links: [
        {
          label: "Global Campaigns (Titles Tab)",
          url: "https://globalc4bcampaigns.fairwaydigitalmedia.com/?tab=titles",
        },
      ],
    },
    { id: "d15", text: "Send APAC Account List to HUT-3", owner: "Marlee" },
  ],
};

const week_2025_11_03 = {
  id: "WK-NOV03",
  date: "Nov 03, 2025",
  title: "Bite-Sized ABM 101 Videos",
  attendees: "Marlee, Benton",
  summary:
    "Kickoff for creating 'Bite-Sized' ABM videos tailored to GTM team. Defined 3 deliverables for Marketing, SDRs, and Sales.",
  tags: ["Video", "Enablement"],
  deliverables: [
    {
      id: "d16",
      text: "Draft content for 3 videos (Marketing, SDR, Sales)",
      owner: "Ben",
      links: [
        { label: "Marketing Doc", url: "#marketing" },
        { label: "SDR Script", url: "#sdr" },
        { label: "Sales Deck", url: "#sales" },
      ],
    },
    {
      id: "d17",
      text: "Check Sales Enablement Hub for elevator pitches",
      owner: "Marlee",
    },
    {
      id: "d18",
      text: "Summarize 1:1 and 1:few plan for Ben",
      owner: "Marlee",
    },
    {
      id: "d19",
      text: "Record videos (Target: Nov 17-20)",
      owner: "Ben",
      links: [
        {
          label: "ABM 101",
          url: "https://assets.fairwaydigitalmedia.com/coursera/videos/abm_101.mp4",
        },
        {
          label: "SDR Video",
          url: "https://assets.fairwaydigitalmedia.com/coursera/videos/coursera_abm_sdr.mp4",
        },
        {
          label: "Sales Video",
          url: "https://assets.fairwaydigitalmedia.com/coursera/videos/sales_accountplans.mp4",
        },
      ],
    },
  ],
};

// --- COMBINED EXPORT ---
export const DATA = [
  week_2026_01_05,
  week_2025_12_15,
  week_2025_12_01,
  week_2025_11_24,
  week_2025_11_17,
  week_2025_11_14,
  week_2025_11_03,
];
